import { type MouseEvent, useEffect, useState } from 'react'

import { Info, Music, Play, Square } from 'lucide-react'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import { Link } from '@/shared/components/atoms/Link'
import { cn, formatDuration } from '@/shared/utils/helpers'

interface PlaylistRowProps {
	playlist: Playlist
	isSelected: boolean
	onSelectionChange: (checked: boolean) => void
	className?: string
	contentClassName?: string
	coverUrl?: string
	coverSizeClassName?: string
	coverImageClassName?: string
	coverFallbackClassName?: string
	coverContainerClassName?: string
	isPlaying: boolean
	isLoadingPlayback?: boolean
	isPlaybackReady?: boolean
	onPlayClick: (event: MouseEvent<HTMLButtonElement>) => void
	disabled?: boolean
	disabledReason?: string
}

export function PlaylistRow({
	playlist,
	isSelected,
	onSelectionChange,
	className,
	contentClassName,
	coverUrl,
	coverSizeClassName = 'w-10 h-10',
	coverImageClassName = 'rounded object-cover',
	coverFallbackClassName = 'bg-muted rounded',
	coverContainerClassName,
	isPlaying,
	isLoadingPlayback = false,
	isPlaybackReady = false,
	onPlayClick,
	disabled = false,
	disabledReason,
}: PlaylistRowProps) {
	const [hasImageError, setHasImageError] = useState(false)

	useEffect(() => {
		setHasImageError(false)
	}, [playlist.id, coverUrl])

	const resolvedCover =
		coverUrl || playlist.getSmallestThumbnail() || '/default-playlist.png'

	const handleRowClick = () => {
		if (!disabled) {
			onSelectionChange(!isSelected)
		}
	}

	return (
		<div
			className={cn(
				'flex items-center justify-between px-4 py-3 border-b last:border-b-0',
				disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent/50',
				className
			)}
		>
			<div
				className={cn(
					'flex items-center gap-3 flex-1',
					disabled ? 'cursor-not-allowed' : 'cursor-pointer',
					contentClassName
				)}
				onClick={handleRowClick}
			>
				<div
					className={cn(
						'relative group overflow-hidden flex-shrink-0',
						coverSizeClassName,
						coverContainerClassName
					)}
					onClick={(event) => event.stopPropagation()}
				>
					{resolvedCover && !hasImageError ? (
						<img
							src={resolvedCover}
							alt={playlist.title}
							className={cn('h-full w-full', coverImageClassName)}
							onError={() => setHasImageError(true)}
						/>
					) : (
						<div
							className={cn(
								'h-full w-full flex items-center justify-center',
								coverFallbackClassName
							)}
						>
							<Music className="h-5 w-5 text-muted-foreground" />
						</div>
					)}
					<button
						type="button"
						onClick={(event) => {
							event.stopPropagation()
							onPlayClick(event)
						}}
						disabled={isLoadingPlayback}
						className={cn(
							'absolute inset-0 flex items-center justify-center bg-black/50 rounded transition-opacity cursor-pointer disabled:cursor-wait',
							isPlaying && isPlaybackReady
								? 'opacity-100'
								: 'opacity-0 group-hover:opacity-100'
						)}
					>
						{isPlaying ? (
							isLoadingPlayback ? (
								<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
							) : (
								<Square className="h-4 w-4 text-white fill-white" />
							)
						) : (
							<Play className="h-4 w-4 text-white fill-white" />
						)}
					</button>
				</div>

				<div className="flex-1 min-w-0">
					<p className="text-sm font-medium truncate">
						<Link
							to={`/music/playlists/${playlist.id}`}
							target="_blank"
							onClick={(event) => event.stopPropagation()}
						>
							{playlist.title}
						</Link>
					</p>
					{playlist.analytics && (
						<p className="text-xs text-muted-foreground">
							{playlist.analytics.count} songs -{' '}
							{formatDuration(playlist.analytics.length)}
						</p>
					)}
					{disabled && disabledReason && (
						<p className="text-xs text-[color:var(--status-warning)] mt-1 flex items-center gap-1">
							<Info className="h-3 w-3 flex-shrink-0" />
							<span>{disabledReason}</span>
						</p>
					)}
				</div>
			</div>

			<Checkbox
				checked={isSelected}
				onCheckedChange={(checked) => onSelectionChange(checked === true)}
				onClick={(event) => event.stopPropagation()}
				disabled={disabled}
			/>
		</div>
	)
}
